import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {
  GeneralViewCompetence,
  Competence,
  NumericMetadata,
  MarkerMetadata,
} from 'utils/types/newReviewed';
import BottomMenu from 'containers/NewReviewed/components/BottomMenu';
import {useMobile} from 'utils/hooks/useMobile';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useEffect} from 'react';
import {Link, useLocation, useParams} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import ItemAccordeon from '../../components/Accordeon/ItemAccordeon';
import Accordeon, {AccordeonItem} from '../../components/Accordeon';
import styles from './GeneralView.module.scss';

const CompetenceVariants: Record<
  NumericMetadata['priority'],
  AccordeonItem['variant']
> = {
  greater: 'positive',
  neutral: 'attention',
  smaller: 'negative',
  second_smaller: 'attention',
};

enum CompetenceMarkerColor {
  attention = 'yellowMarker',
  positive = 'greenMarker',
  negative = 'redMarker',
}

interface GeneralViewProps {
  reviewGridName?: string;
  competences?: Competence[];
  comments?: NumericMetadata;
  totalScore: number | string | undefined;
  textMarkers?: Record<string, MarkerMetadata[]>;
  onMarkerChange(marker: any): any; // TODO: verify types
}

const CompetenceIcon: Record<number, IconProp> = {
  // Empty
  0: ['fas', 'spinner'],
  // ENEM
  97: ['fad', 'typewriter'],
  98: ['fad', 'books'],
  99: ['fad', 'comments-question-check'],
  100: ['fad', 'book-user'],
  101: ['fad', 'person-sign'],
  // Multigenre
  195: ['fad', 'typewriter'],
  196: ['fad', 'comments-question-check'],
  197: ['fad', 'book-open-cover'],
  198: ['fad', 'book-user'],
  199: ['fad', 'head-side-gear'],
  200: ['fad', 'message-quote'],
};

function GeneralView({
  comments,
  totalScore = 0,
  competences,
  textMarkers,
  reviewGridName,
  onMarkerChange,
}: GeneralViewProps): JSX.Element {
  const {isMobile} = useMobile();
  const {trackEvent} = useTrackEvent();

  const location = useLocation();
  const {composition_id} = useParams();

  const isENEM = !!reviewGridName?.match(/ENEM/i);

  const getOrthographicDeviations = isENEM
    ? getMarkersByCompetenceId(97)
    : getMarkersByCompetenceId(195);

  const orthographicDeviations = getOrthographicDeviations
    .flat()
    .map(({text, suggested_text, comment}) => ({
      text,
      suggested_text,
      comment,
    }));

  useEffect(() => {
    trackEvent('step3_feedback_all_competencies_screen_view', {
      composition: {
        composition_id: Number(composition_id),
      },
    });
  }, []);

  function getCompositionCompetences(): GeneralViewCompetence[] {
    const metadatas = Object.values(comments ?? {}) as NumericMetadata[];

    // Manually inserting 3 mock empty competences for when
    // the middle ones (second smaller and neutrals) are not yet processed
    // to display the loading variant of their accordeon items
    if (metadatas.length === 2) {
      const mockEmptyCompetence: NumericMetadata = {
        palavras: {value: 0},
        paragrafos: {value: 0},
        pontos: {value: 0},
        virgulas: {value: 0},
        competence_id: 0,
        comment: {
          mandou_bem: '',
          podemos_melhorar: '',
        },
        indicator: '',
        priority: 'neutral',
        score: 0,
      };
      metadatas.splice(
        1,
        0,
        mockEmptyCompetence,
        mockEmptyCompetence,
        mockEmptyCompetence,
      );
    }

    return metadatas
      .map((metadata: NumericMetadata) => {
        const competence = getCompetenceById(metadata.competence_id);
        const boundaries = getMarkersByCompetenceId(metadata.competence_id);

        // TODO: Type this object correctly
        const serializedCompetence = {
          icon: CompetenceIcon[metadata.competence_id] ?? 'check',
          readByUser: true, // TODO: waiting fot the seen by user endpoint
          boundaries,
          score: metadata.score,
          name: competence?.name,
          nickname: competence?.nickname,
          variant:
            metadata.competence_id === 0
              ? 'empty'
              : CompetenceVariants[metadata.priority ?? 'neutral'],
          positiveComment: metadata.comment?.mandou_bem,
          attentionComment: metadata.comment?.podemos_melhorar,
          evidenceHighlight: metadata.indicator,
          priority: metadata.priority,
          id: metadata.competence_id,
        };

        return {
          ...serializedCompetence,
          onClick: (isOpen: boolean) =>
            onCompetenceClick(isOpen, serializedCompetence),
        };
      })
      .sort((compA, compB) => {
        const priorityOrder: Record<NumericMetadata['priority'], number> = {
          smaller: 1,
          second_smaller: 2,
          neutral: 3,
          greater: 4,
        };

        const isPriorityEqual =
          priorityOrder[compA.priority] - priorityOrder[compB.priority] === 0;

        if (isPriorityEqual) {
          return compA.score - compB.score;
        }

        return priorityOrder[compA.priority] - priorityOrder[compB.priority];
      });
  }

  function getCompetenceById(competenceId: number) {
    return competences?.find((competence) => competence.id === competenceId);
  }

  function getMarkersByCompetenceId(competenceId: number): any {
    // TODO: verify types
    const metadatas = Object?.values(textMarkers ?? {});

    const markers = metadatas.map((metadata) =>
      metadata?.filter((marker) => {
        return marker.competence_id === competenceId;
      }),
    );

    return markers;
  }

  function onCompetenceClick(
    isOpen: boolean,
    competence: GeneralViewCompetence,
  ) {
    const markerColor = CompetenceMarkerColor[competence.variant];
    const competenceMarkers = getMarkersByCompetenceId(competence.id);

    const competenceHighlights = Object.values(competenceMarkers ?? {})
      .flat()
      .map((marker: any, index: number) => {
        // TODO: verify types
        return isOpen
          ? {
              competence_id: marker?.competence_id,
              id: index,
              start: marker?.boundaries?.[0],
              end: marker?.boundaries?.[1],
              paragraph: marker?.paragraph,
              className: markerColor,
            }
          : {};
      });

    onMarkerChange(competenceHighlights);
  }

  return (
    <section className={styles.competences} data-focusid="accordeon">
      <div className={styles.fullGrade}>
        <p>
          Nota total do texto: <b>{totalScore} pontos</b>
        </p>
      </div>

      {isMobile ? (
        <BottomMenu
          items={getCompositionCompetences()}
          progressLimit={isENEM ? 200 : 10}
        />
      ) : (
        <Accordeon
          // TODO: Resolve redundancy with bellow .map() and getCompositionCompetences .map()
          items={getCompositionCompetences().map((competence) => ({
            id: competence.id,
            content: (
              <ItemAccordeon
                positiveComment={competence.positiveComment}
                attentionComment={competence.attentionComment}
                variant={competence.variant}
                evidenceHighlight={competence.evidenceHighlight}
                competenceId={competence.id}
                orthographicDeviations={orthographicDeviations}
                shouldShowPracticeC5Button={
                  isENEM && competence.id === 101 && competence.score <= 120
                }
                score={competence.score}
                isEnem={isENEM}
              />
            ),
            icon: competence.icon,
            score: competence.score,
            subtitle: competence.name,
            title: competence.nickname,
            variant: competence.variant,
            progressLimit: isENEM ? 200 : 10,
            titleRight: competence.readByUser ? undefined : 'Não lido',
            onClick: (isOpen) => onCompetenceClick(isOpen, competence),
          }))}
        />
      )}

      {isENEM && (
        <Link
          to={{
            pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/indicadores`,
            search: location.search,
          }}
          onClick={() => {
            trackEvent('access_structure_indicators_button_clicked', {
              composition: {
                composition_id: Number(composition_id),
              },
            });
            onCompetenceClick(false, {} as GeneralViewCompetence);
          }}
        >
          Confira todos os Elementos do Texto
          <FontAwesomeIcon
            icon={['fad', 'circle-plus']}
            color="#2A5082"
            size="lg"
          />
        </Link>
      )}
    </section>
  );
}

export default GeneralView;
